import React, { useEffect, useState } from 'react'
import { KeyRound } from 'lucide-react'
import { authApi, onReauth } from '../lib/api'
import { appBridge } from '../lib/app/bridge'
import { useT } from '../i18n'

/**
 * Sensitive actions (API keys, webhooks, mail and sign-in settings, admin rights, VPN downloads,
 * deleting sites …) need the password confirmed in the last 10 minutes. When the API asks for it,
 * this asks the person; the action then goes through by itself. Signed in with single sign-on:
 * confirming happens at the identity provider, after which the person repeats the action — in the
 * app, in the phone's browser sheet, after which the action goes through by itself.
 */
export function ReauthPrompt({ sso }: { sso: boolean }) {
  const t = useT()
  const [pending, setPending] = useState<((ok: boolean) => void) | null>(null)
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    onReauth(() => new Promise<boolean>(resolve => setPending(() => resolve)))
    return () => onReauth(null)
  }, [])
  if (!pending) return null

  const done = (ok: boolean) => { pending(ok); setPending(null); setPassword(''); setError('') }
  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true); setError('')
    try { await authApi.reauth(password); done(true) }
    catch (err) { setError((err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? t('common.tryAgain')) }
    finally { setBusy(false) }
  }

  return (
    <div className="modal-overlay" onKeyDown={e => { if (e.key === 'Escape') done(false) }}>
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby="reauth-title" style={{ maxWidth: 420 }}>
        <div className="modal-header">
          <h2 className="modal-title" id="reauth-title"><KeyRound size={18} style={{ verticalAlign: -3, marginRight: 8 }} />{t('auth.reauth.title')}</h2>
        </div>
        {sso ? (
          <>
            <p style={{ fontSize: 13, color: 'var(--fg-2)', marginBottom: 16 }}>
              {t('auth.reauth.sso')}
            </p>
            {error && <div className="alert-bar alert-bar-error" role="alert" style={{ marginBottom: 12 }}>{error}</div>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-ghost" onClick={() => done(false)}>{t('common.cancel')}</button>
              <button type="button" className="btn btn-primary" autoFocus disabled={busy} onClick={async () => {
                setBusy(true); setError('')
                const ret = window.location.pathname + window.location.search
                const app = appBridge()
                try {
                  if (!app?.features.browserSignIn) { window.location.href = (await authApi.ssoReauth(ret)).data.data.url; return }
                  const back = await app.signInWithBrowser((await authApi.ssoReauth(ret, true)).data.data.url)
                  setBusy(false)
                  if (back.done) done(true)
                  else if (back.error) setError(back.error)
                }
                catch (err) { setError((err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? t('auth.reauth.idpUnreachable')); setBusy(false) }
              }}>{t('common.continue')}</button>
            </div>
          </>
        ) : (
          <form onSubmit={submit}>
            <p style={{ fontSize: 13, color: 'var(--fg-2)', marginBottom: 16 }}>
              {t('auth.reauth.password')}
            </p>
            {error && <div className="alert-bar alert-bar-error" role="alert" style={{ marginBottom: 12 }}>{error}</div>}
            <div className="form-field">
              <label className="label" htmlFor="reauth-password">{t('common.password')}</label>
              <input id="reauth-password" className="input" type="password" required autoFocus autoComplete="current-password"
                value={password} onChange={e => setPassword(e.target.value)} />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-ghost" onClick={() => done(false)}>{t('common.cancel')}</button>
              <button type="submit" className="btn btn-primary" disabled={busy || !password}>
                {busy ? <span className="spinner" role="img" aria-label={t('common.working')} style={{ width: 16, height: 16 }} /> : t('common.confirm')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
